import * as React from 'react';
import {
  CheckCircle, Circle, AlertCircle, Clock, RefreshCw, Download, XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type StatusVariant =
  | 'active' | 'inactive' | 'error' | 'warning' | 'starting'
  | 'stopping' | 'installing' | 'running' | 'pending' | 'success' | 'healthy' | 'unhealthy';

const STATUS_CONFIG: Record<StatusVariant, {
  label: string;
  icon: React.ElementType;
  className: string;
}> = {
  active:     { label: 'Active',     icon: CheckCircle, className: 'bg-cs-success-bg text-cs-success-400 border-cs-success-500/30' },
  healthy:    { label: 'Healthy',    icon: CheckCircle, className: 'bg-cs-success-bg text-cs-success-400 border-cs-success-500/30' },
  success:    { label: 'Success',    icon: CheckCircle, className: 'bg-cs-success-bg text-cs-success-400 border-cs-success-500/30' },
  running:    { label: 'Running',    icon: CheckCircle, className: 'bg-cs-success-bg text-cs-success-400 border-cs-success-500/30' },
  inactive:   { label: 'Inactive',   icon: Circle,      className: 'bg-cs-surface-raised text-cs-text-muted border-cs-border' },
  pending:    { label: 'Pending',    icon: Circle,      className: 'bg-cs-surface-raised text-cs-text-muted border-cs-border' },
  error:      { label: 'Error',      icon: AlertCircle, className: 'bg-cs-error-bg text-cs-error-400 border-cs-error-500/30' },
  unhealthy:  { label: 'Unhealthy',  icon: AlertCircle, className: 'bg-cs-error-bg text-cs-error-400 border-cs-error-500/30' },
  warning:    { label: 'Warning',    icon: AlertCircle, className: 'bg-cs-warning-bg text-cs-warning-400 border-cs-warning-500/30' },
  starting:   { label: 'Starting',   icon: Clock,       className: 'bg-cs-warning-bg text-cs-warning-400 border-cs-warning-500/30' },
  stopping:   { label: 'Stopping',   icon: Clock,       className: 'bg-cs-warning-bg text-cs-warning-400 border-cs-warning-500/30' },
  installing: { label: 'Installing', icon: Download,    className: 'bg-cs-info-bg text-cs-info-400 border-cs-info-500/30 animate-pulse-subtle' },
};

interface StatusBadgeProps {
  variant: StatusVariant | string;
  label?: string;
  className?: string;
  showIcon?: boolean;
  animate?: boolean;
}

export function StatusBadge({ variant, label, className, showIcon = true }: StatusBadgeProps) {
  const cfg = STATUS_CONFIG[variant as StatusVariant] ?? {
    label: variant,
    icon: Circle,
    className: 'bg-cs-surface-raised text-cs-text-muted border-cs-border',
  };
  const Icon = cfg.icon;
  const isSpinning = variant === 'starting' || variant === 'stopping' || variant === 'installing';

  return (
    <span
      role="status"
      aria-label={`Status: ${label ?? cfg.label}`}
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded border font-medium',
        cfg.className,
        className
      )}
    >
      {showIcon && (
        <Icon
          className={cn('w-3 h-3 flex-shrink-0', isSpinning && 'animate-spin')}
          aria-hidden="true"
        />
      )}
      {label ?? cfg.label}
    </span>
  );
}
